import React from 'react';
import { useAppSelector } from '../../store/hooks';

export interface Column<T = any> {
  key: string;
  header: string;
  render?: (row: T) => React.ReactNode;
  width?: string;
}

interface AdminDataTableProps<T = any> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  loading?: boolean;
  emptyMessage?: string;
}

function AdminDataTable<T extends Record<string, any>>({
  columns, data, onRowClick, loading = false, emptyMessage = 'No data found',
}: AdminDataTableProps<T>) {
  const colors = useAppSelector((state) => state.theme.theme);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: colors.primary, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm" style={{ color: colors.grayScale500 }}>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl" style={{ border: `1px solid ${colors.borderColor}` }}>
      <table className="w-full text-sm">
        <thead>
          <tr style={{ backgroundColor: colors.inputBackground }}>
            {columns.map((col) => (
              <th
                key={col.key}
                className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider"
                style={{ color: colors.grayScale500, width: col.width }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr
              key={idx}
              onClick={() => onRowClick?.(row)}
              className={`transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
              style={{ borderTop: `1px solid ${colors.borderColor}` }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.hoverBackground || `${colors.primary}08`; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {columns.map((col) => (
                <td key={col.key} className="px-4 py-3" style={{ color: colors.textColor }}>
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AdminDataTable;
